<template>
  <!-- 外层承接布局 class，便于 IntersectionObserver；内层 img 铺满 -->
  <span ref="rootRef" class="encrypted-img-root" :class="imgClass">
    <img
      v-if="displaySrc"
      :src="displaySrc"
      :alt="alt"
      :width="width"
      :height="height"
      class="encrypted-img-el"
      :style="{ objectFit: fit }"
      :loading="eager ? 'eager' : 'lazy'"
      decoding="async"
      :fetchpriority="fetchPriority || undefined"
      :crossorigin="crossorigin || undefined"
      @load="onImgLoad"
      @error="onImgError"
    />
    <span v-else class="encrypted-img-ph" aria-hidden="true" />
  </span>
</template>

<script setup lang="ts">
/**
 * 资源库图片：url + key 成对。
 * - 视口内再下载/解密（eager 可跳过）
 * - 密文 blob 走共享缓存，卸载时 release
 * - width/height 只作 HTML 属性（比例提示），不写进 CSS，避免栅格缩宽后被固定高度拉变形
 */
import { releaseEncryptedImage, resolveEncryptedImage } from '~/utils/bca1'

const props = withDefaults(
  defineProps<{
    url?: string | null
    /** 64 位 hex；非空时 url 为 BCA1 密文容器 */
    keyHex?: string | null
    alt?: string
    width?: number | string
    height?: number | string
    imgClass?: string
    /** 解密失败或无图时的占位图（可选） */
    placeholder?: string
    crossorigin?: '' | 'anonymous' | 'use-credentials'
    /** true：立刻加载（首屏 Banner / 分类 Tab） */
    eager?: boolean
    /** high | low | auto */
    fetchPriority?: 'high' | 'low' | 'auto'
    fit?: 'cover' | 'contain'
  }>(),
  {
    url: '',
    keyHex: '',
    alt: '',
    placeholder: '',
    crossorigin: '',
    eager: false,
    fit: 'cover',
  },
)

const emit = defineEmits<{
  load: [e: Event]
  error: [e?: Event]
}>()

const rootRef = ref<HTMLElement | null>(null)
const inView = ref(props.eager)
const displaySrc = ref('')
/** 当前持有的密文 blob 对应入参，用于 release */
let heldUrl = ''
let heldKey = ''
let seq = 0
let io: IntersectionObserver | null = null

function releaseHeld() {
  if (heldUrl && heldKey) {
    releaseEncryptedImage(heldUrl, heldKey)
  }
  heldUrl = ''
  heldKey = ''
}

function onImgLoad(e: Event) {
  emit('load', e)
}

function onImgError(e: Event) {
  emit('error', e)
}

async function load() {
  const mySeq = ++seq
  releaseHeld()
  displaySrc.value = ''

  if (!inView.value) return

  const url = props.url
  const keyHex = props.keyHex

  try {
    const resolved = await resolveEncryptedImage(url, keyHex)
    if (mySeq !== seq) {
      if (resolved.startsWith('blob:') && (keyHex ?? '').trim()) {
        releaseEncryptedImage(url, keyHex)
      }
      return
    }
    if (resolved.startsWith('blob:') && (keyHex ?? '').trim()) {
      heldUrl = (url ?? '').trim()
      heldKey = (keyHex ?? '').trim()
    }
    displaySrc.value = resolved || props.placeholder || ''
    if (!displaySrc.value) emit('error')
  } catch {
    if (mySeq !== seq) return
    displaySrc.value = props.placeholder || ''
    if (!displaySrc.value) emit('error')
  }
}

function setupIo() {
  io?.disconnect()
  io = null
  if (props.eager || typeof IntersectionObserver === 'undefined') {
    inView.value = true
    return
  }
  const el = rootRef.value
  if (!el) return
  io = new IntersectionObserver(
    entries => {
      if (entries.some(e => e.isIntersecting)) {
        inView.value = true
        io?.disconnect()
        io = null
      }
    },
    { rootMargin: '120px 0px', threshold: 0.01 },
  )
  io.observe(el)
}

watch(
  () => [props.url, props.keyHex, props.placeholder, inView.value] as const,
  () => {
    void load()
  },
)

watch(
  () => props.eager,
  () => {
    if (props.eager) inView.value = true
    else setupIo()
  },
)

onMounted(() => {
  if (props.eager) {
    inView.value = true
    void load()
    return
  }
  setupIo()
  // IO 可能同步触发；若仍未进视口则等回调
  if (inView.value) void load()
})

onBeforeUnmount(() => {
  seq += 1
  io?.disconnect()
  io = null
  releaseHeld()
})
</script>

<style scoped lang="scss">
.encrypted-img-root {
  display: block;
  box-sizing: border-box;
  line-height: 0;
}

.encrypted-img-el {
  display: block;
  width: 100%;
  height: 100%;
}

.encrypted-img-ph {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 1em;
  border-radius: inherit;
  background: color-mix(in srgb, var(--muted, #888) 22%, transparent);
}
</style>
